import React, { lazy, useEffect, useRef, useState } from "react";
import { PREMIUM, TPropertyCardMdl } from "properties/_models/PropertyMdl";
import styles from "./_css/propertyCard.module.css";
import "./_css/propertyCard.css";
import SwiperCore, { Navigation } from "swiper";
import "react-lazy-load-image-component/src/effects/blur.css";
import "swiper/swiper-bundle.min.css";
import clsx from "clsx";
import { URLS } from "_configs/URLS";
import { Helmet } from "react-helmet-async";
import Suspenser from "_common/loaders/Suspenser";
import { getWebpFile } from "_common/_utils/fileUtils";
import { analyticsUtils } from "_common/_utils/analyticsUtils";
import { useLocation } from "react-router";
import { PLACEMENT_EVENT } from "placements/_models/PlacementsMdl";
import { observer } from "mobx-react";
import { PropertyGalleryCardFallback } from "properties/PropertyGalleryCardFallback";
import { refreshPage } from "_common/_utils/pageUtils";
import { Link } from "react-router-dom";
import { useTrackingPropertyCard } from "_common/_utils/useTrackingPropertyCard";
import { PropertyCardContent } from "properties/PropertyCardContent";
import PropertyCardTags from "properties/PropertyCardTags";

SwiperCore.use([Navigation]);

type Props = {
    property: TPropertyCardMdl;
    isMapCard?: boolean;
    isFeaturedCard?: boolean;
    isSimilarCard?: boolean;
    pageBuilder?: boolean;
    onEdit?: () => void;
    onMouseOver?: () => void;
    onMouseLeave?: () => void;
};

const LazyPropertyGalleryCard = lazy(() => import("properties/PropertyGalleryCard"));
// const LazyPropertyCardTags = lazy(() => import("properties/PropertyCardTags"));

export const PropertyCard = observer((props: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const url = useLocation().pathname;
    const [isFeatured, setIsFeatured] = useState(false);

    useTrackingPropertyCard(ref, props);

    useEffect(() => {
        setIsFeatured(
            Boolean(
                props.isFeaturedCard ||
                    props.property.isFeatured ||
                    props.property.premium.premiumPlan === PREMIUM.EXCLUSIVE,
            ),
        );
    }, [props.isFeaturedCard, props.property.isFeatured]);

    const onClick = (e: React.MouseEvent) => {
        if (props.property.placementId && (props.isFeaturedCard || props.property.isPremium)) {
            if (props.isFeaturedCard) {
                void analyticsUtils.tracksEventOfItem(
                    PLACEMENT_EVENT.VISITED_FEATURED,
                    props.property.placementId,
                    url,
                );
            } else if (props.property.isPremium) {
                void analyticsUtils.tracksEventOfItem(PLACEMENT_EVENT.VISITED_PREMIUM, props.property.placementId, url);
            }
        }

        if (props.isSimilarCard) {
            e.preventDefault();
            refreshPage(URLS.property(props.property.localized.urlAlias));
        }
    };

    return (
        <div
            key={props.property._id}
            ref={ref}
            className={clsx(styles.container, "flex_column flex-1", {
                [styles.containerMap]: props.isMapCard,
                [styles.containerFeatured]: props.isFeaturedCard || props.isSimilarCard,
                ["m_0"]: props.isFeaturedCard,
                [styles.borderYellow]: isFeatured,
                [styles.aspectBuilder]: !!props.pageBuilder,
            })}
            onMouseOver={props.onMouseOver}
            onMouseLeave={props.onMouseLeave}
        >
            <Helmet>
                <link
                    rel="preload"
                    as="image"
                    href={getWebpFile(props.property.photos?.[0]?.thumbnail) ?? props.property.photos?.[0]?.url}
                />
            </Helmet>
            <Link
                to={URLS.property(props.property.localized.urlAlias)}
                onClick={onClick}
                aria-label={`Property link for "${props.property.localized.title}"`}
                aria-labelledby={`Property link for "${props.property.localized.title}"`}
                className={clsx("flex-1 flex position_relative", styles.containerGallery, {
                    [styles.containerGalleryMap]: props.isMapCard,
                })}
            >
                <Suspenser fallback={<div />}>
                    {/*<LazyPropertyCardTags property={props.property} isMapCard={props.isMapCard} />*/}
                    <PropertyCardTags property={props.property} isMapCard={props.isMapCard} isFeatured={isFeatured} />
                </Suspenser>
                <Suspenser fallback={<PropertyGalleryCardFallback {...props} />}>
                    <LazyPropertyGalleryCard {...props} />
                </Suspenser>
            </Link>
            <PropertyCardContent {...props} onClick={onClick} />
        </div>
    );
});
